import React, { useEffect } from 'react';

import { Dropdown, Form, Input } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { PatchedPatchEmployee } from '../../../../store/generated/portal/employee/employees';
import { EmployeeData } from './MemberEdit';
import {
  MEMBER_ROLE,
  getCurrentOrganization,
  isCurrentUserStaffOrSuperAdmin,
} from 'common/selectors/auth';
import styled from 'styled-components';
import { ValidateEmail } from 'common/utils/EmailUtils';
import { useGetEmployeeTeamsQuery } from 'areas/team-management/api/teamApi';
import LoadingSpinner from 'common/components/LoadingSpinner';
import PositionedModal from 'common/components/PositionedModal';
import { StyledAddButton, StyledButton, StyledCancelButton, StyledDeleteButton } from 'common/components/button/StyledButton';
import { useToasts } from 'common/toast';
import { useAddTeamMemberMutation, useGetAllTeamsQuery, useRemoveTeamMemberMutation } from 'areas/team-management/api/teamApi';
import { END_OF_SERVICE_REASONS } from '../utils/endOfServiceReasons';

const StatusPill = styled.div<{ verified: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  line-height: 1;
  font-weight: 500;
  background-color: ${props => (props.verified ? '#e6f4ea' : '#fce8e6')};
  color: ${props => (props.verified ? '#137333' : '#c5221f')};
`;

const TeamList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px 10px; /* row-gap column-gap: compact horizontal spacing, smaller vertical spacing */
`;

const TeamFieldWrapper = styled.div`
  width: 100%;
  min-height: 40px;
  border: 1px solid #d4d4d5;
  border-radius: 6px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
`;

const TeamListItem = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px; /* slightly reduced gap between swatch and name */
  padding: 4px 28px 4px 8px;
  border-radius: 6px;
  white-space: nowrap;
  background: #fff;
  transition: background 120ms ease-in-out;

  &:hover {
    background: #f3f3f3;
  }

  &:hover button {
    opacity: 1;
  }
`;

const TeamDeleteButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: rgba(0, 0, 0, 0.08);
  color: #000;
  border-radius: 999px;
  opacity: 0;
  transition: opacity 120ms ease-in-out;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.18);
  }
`;

const TeamColor = styled.div<{ color?: string | null }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  border: 1px solid #e0e0e0;
  background: ${props => props.color || 'transparent'};
`;

const EmailInputWrapper = styled.div`
  position: relative;
  display: block;
  width: 100%;

  /* Ensure Semantic UI input fills the wrapper and inner input reserves space for the status pill */
  .ui.input {
    width: 100%;
  }
  .ui.input > input {
    padding-right: 130px !important;
  }
`;

const CompactStatusPill = styled(StatusPill)`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  pointer-events: none; /* non-interactive */
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,0.06);
`;



interface MemberProfileTabProps {
  formData: PatchedPatchEmployee;
  employeeData: EmployeeData;
  onInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  onNumberInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDropdownChange: (e: React.SyntheticEvent, data: any) => void;
}

const MemberEditProfileTab: React.FC<MemberProfileTabProps> = ({
  formData,
  employeeData,
  onInputChange,
  onDropdownChange,
}) => {
  const currentOrganization = useSelector(getCurrentOrganization);
  const isStaffOrSuperUser = useSelector(isCurrentUserStaffOrSuperAdmin);
  const isAccessLevelDisabled = !isStaffOrSuperUser && (currentOrganization?.tier ?? 0) < 2;

  const { data: employeeTeamsData, isLoading: isTeamsLoading } = useGetEmployeeTeamsQuery(
    { employee_id: employeeData?.id },
    { skip: !employeeData?.id }
  );

  const teams = employeeTeamsData?.results || [];

  // Join Team modal state
  const [joinModalOpen, setJoinModalOpen] = React.useState(false);
  const [selectedTeamId, setSelectedTeamId] = React.useState<number | null>(null);
  const [deleteMembership, setDeleteMembership] = React.useState<any | null>(null);
  const [addTeamMember, { isLoading: isAdding }] = useAddTeamMemberMutation();
  const [removeTeamMember, { isLoading: isRemoving }] = useRemoveTeamMemberMutation();
  const { addToast } = useToasts();

  const { data: allTeamsData } = useGetAllTeamsQuery(undefined, { skip: !employeeData?.id });
  const isArchivedMember = employeeData?.status === 99;

  // teams not currently a member of
  const availableTeams = (allTeamsData?.results || []).filter((t: any) => !teams.some((et: any) => et.team_id === t.id)).sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));

  const teamOptions = availableTeams.map((t: any) => ({ key: t.id, text: t.name, value: t.id }));

  const refetchEmployeeTeams = (employeeTeamsData as any)?.refetch;

  const handleJoinSave = async () => {
    if (!selectedTeamId || !employeeData?.id) return;
    try {
      await addTeamMember({ employee: employeeData.id, team: selectedTeamId }).unwrap();
      addToast('Team membership added', { appearance: 'success', autoDismiss: true });
      setJoinModalOpen(false);
      setSelectedTeamId(null);
      // refetch the employee teams if possible
      if (typeof refetchEmployeeTeams === 'function') {
        try { await refetchEmployeeTeams(); } catch (e) { /* ignore */ }
      }
    } catch (err) {
      console.error('Failed to add team member', err);
      addToast('Failed to add team member', { appearance: 'error', autoDismiss: true });
    }
  };

  const handleRemoveTeamMember = async () => {
    if (!deleteMembership?.id) return;
    try {
      await removeTeamMember(deleteMembership.id).unwrap();
      addToast('Team membership removed', { appearance: 'success', autoDismiss: true });
      setDeleteMembership(null);
      if (typeof refetchEmployeeTeams === 'function') {
        try { await refetchEmployeeTeams(); } catch (e) { /* ignore */ }
      }
    } catch (err) {
      console.error('Failed to remove team member', err);
      addToast('Failed to remove team member', { appearance: 'error', autoDismiss: true });
    }
  };

  return (
    <Form>
      <Form.Group inline>
        <label style={{ minWidth: '170px' }}>First Name</label>
        <Form.Field required style={{ flex: 1 }}>
          <Input
            name="first_name"
            placeholder="Enter first name"
            value={formData.first_name || ''}
            onChange={onInputChange}
            error={!formData.first_name}
            fluid
          />
        </Form.Field>
      </Form.Group>

      <Form.Group inline>
        <label style={{ minWidth: '170px' }}>Last Name</label>
        <Form.Field required style={{ flex: 1 }}>
          <Input
            name="last_name"
            placeholder="Enter last name"
            value={formData.last_name || ''}
            onChange={onInputChange}
            error={!formData.last_name}
            fluid
          />
        </Form.Field>
      </Form.Group>

      <Form.Group inline>
        <label style={{ minWidth: '170px' }}>Email</label>
        <Form.Field style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <EmailInputWrapper style={{ flex: 1 }}>
              <Input
                placeholder="Enter email"
                name="email"
                type="email"
                value={formData.email || ''}
                fluid
                onChange={onInputChange}
                error={
                  !formData.email ||
                  (!!formData.email && !ValidateEmail(formData.email))
                }
              />

              {/*  TP: This feature does not seem to be working correctly, so I have commented it out for now. We can revisit this later if needed.*/}
              {/*             
              <CompactStatusPill verified={employeeData?.email_verified ?? false}>
                {employeeData?.email_verified ? 'Verified' : 'Not Verified'}
              </CompactStatusPill>
            */}
            </EmailInputWrapper>
          </div>
        </Form.Field>
      </Form.Group>

      {isArchivedMember && (
        <>
          <Form.Group inline>
            <label style={{ minWidth: '170px' }}>Departure Date</label>
            <Form.Field style={{ flex: 1 }}>
              <Input
                name="departure_date"
                type="date"
                value={(formData as any).departure_date || ''}
                onChange={onInputChange}
                fluid
              />
            </Form.Field>
          </Form.Group>

          <Form.Group inline>
            <label style={{ minWidth: '170px' }}>Departure Reason</label>
            <Form.Field style={{ flex: 1 }}>
              <Dropdown
                placeholder="Select departure reason"
                fluid
                selection
                clearable
                name="departure_reason"
                value={(formData as any).departure_reason || undefined}
                onChange={onDropdownChange}
                options={END_OF_SERVICE_REASONS.map(reason => ({
                  key: reason.value,
                  text: reason.label,
                  value: reason.value,
                }))}
              />
            </Form.Field>
          </Form.Group>
        </>
      )}

      <Form.Group inline>
        <label style={{ minWidth: '170px' }}>Access Level</label>
        <Form.Field required style={{ flex: 1 }}>
          <Dropdown
            placeholder="Select access level"
            fluid
            selection
            name="account_type"
            value={formData.account_type}
            onChange={onDropdownChange}
            disabled={isAccessLevelDisabled}
            options={[
              { key: 'member', value: MEMBER_ROLE.REGULAR_USER, text: 'Member' },
              { key: 'team-leader', value: MEMBER_ROLE.TEAM_LEADER, text: 'Team Leader' },
              { key: 'org-admin', value: MEMBER_ROLE.ORG_ADMIN, text: 'Organization Admin' },
            ]}
          />
        </Form.Field>
      </Form.Group>



      <Form.Group inline>
        <label style={{ minWidth: '170px' }}>Survey Participation</label>
        <Form.Field required style={{ flex: 1 }}>
          <Dropdown
            placeholder="Select survey participation status"
            fluid
            selection
            name="survey_status"
            value={formData.survey_status}
            onChange={onDropdownChange}
            options={[
              { key: 1, value: 1, text: 'Survey Participant' },
              { key: 2, value: 2, text: 'Does Not Participate' },
            ]}
          />
        </Form.Field>
      </Form.Group>

      <Form.Group inline>
        <label style={{ minWidth: '170px' }}>Team External ID</label>
        <Form.Field style={{ flex: 1 }}>
          <Input
            name="team_external_id"
            placeholder="Enter team external ID"
            value={formData.team_external_id || ''}
            onChange={onInputChange}
            fluid
          />
        </Form.Field>
      </Form.Group>

      <Form.Group inline style={{ alignItems: 'center' }}>
        <label style={{ minWidth: '170px' }}>Teams</label>
        <Form.Field style={{ flex: 1 }}>
          {isTeamsLoading ? (
            <TeamFieldWrapper>
              <LoadingSpinner />
            </TeamFieldWrapper>
          ) : (
            <TeamFieldWrapper>
              {teams.length === 0 ? (
                <div style={{ color: 'gray', paddingRight: 10, paddingLeft: 10 }}>Not a member of any teams</div>
              ) : (
                <TeamList>
                  {teams.map((t: any) => (
                    <TeamListItem key={t.id}>
                      <TeamColor color={t.team_color} />
                      <span>{t.team_name}</span>
                      <TeamDeleteButton
                        aria-label={`Remove ${t.team_name} from user`}
                        onClick={() => setDeleteMembership(t)}
                        type="button"
                      >
                        ×
                      </TeamDeleteButton>
                    </TeamListItem>
                  ))}
                </TeamList>
              )}
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                <StyledAddButton label="Join Team" color="green" width="100px" onClick={() => setJoinModalOpen(true)} />
              </div>
            </TeamFieldWrapper>
          )}
        </Form.Field>
      </Form.Group>

      <PositionedModal open={joinModalOpen} onClose={() => setJoinModalOpen(false)} size="small">
        <div style={{ padding: 18 }}>
          <h3>Join Team</h3>
          <p>Add this member to the selected team...</p>

          <div style={{ marginTop: 12 }}>
            <Dropdown
              placeholder="Select a team"
              fluid
              selection
              options={teamOptions}
              value={selectedTeamId ?? undefined}
              onChange={(_, data) => setSelectedTeamId(Number(data.value))}
              disabled={teamOptions.length === 0}
              noResultsMessage="No teams available"
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <StyledCancelButton onClick={() => setJoinModalOpen(false)} />
            <StyledButton label={isAdding ? 'Saving...' : 'Save'} color="green" width="110px" onClick={handleJoinSave} disabled={!selectedTeamId || isAdding || teamOptions.length === 0} icon={undefined} />
          </div>
        </div>
      </PositionedModal>

      <PositionedModal open={Boolean(deleteMembership)} onClose={() => setDeleteMembership(null)} size="small">
        <div style={{ padding: 18 }}>
          <h3>Remove Team Membership</h3>
          <p>
            Are you sure you want to remove <strong>{deleteMembership?.team_name}</strong> from this user?
          </p>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <StyledCancelButton onClick={() => setDeleteMembership(null)} disabled={isRemoving} />
            <StyledDeleteButton
              width="140px"
              onClick={handleRemoveTeamMember}
              disabled={isRemoving}
              label={isRemoving ? 'Removing...' : 'Remove'}
            />
          </div>
        </div>
      </PositionedModal>
    </Form>);
}

export default MemberEditProfileTab;
